import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, NgZone, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Subject, catchError, finalize, switchMap, takeUntil, tap } from 'rxjs';
import { ProjectService } from '../../../core/services/project.service';
import { ApplicationContext } from '../../../core/models/_Base/application-context.model';

interface MonitorConnectionTokens {
    accessToken: string;
    dataToken: string;
}

interface MonitorApplicationContext extends ApplicationContext, MonitorConnectionTokens {}

@Component({
    selector: 'app-application-monitor',
    standalone: false,
    templateUrl: './project-run.html',
    styleUrls: ['./project-run.css']
})
export class ProjectRun implements OnInit, OnDestroy {
    @ViewChild('monitorWindow', { static: true }) monitorWindow!: ElementRef<HTMLElement>;
    @ViewChild('applicationFrame') applicationFrame?: ElementRef<HTMLIFrameElement>;

    @Output() applicationData = new EventEmitter<unknown>();
    @Output() applicationConnected = new EventEmitter<void>();
    @Output() connectionChanged = new EventEmitter<boolean>();
    @Output() applicationClosed = new EventEmitter<void>();

    applicationTitle = 'Uygulama';
    applicationVersion = '';
    connected = false;
    isClosed = false;
    projectId = '';
    isLoading = false;
    safeApplicationUrl: SafeResourceUrl | null = null;
    isExpanded = false;
    errorMessage = '';

    private readonly destroyed$ = new Subject<void>();
    private applicationContext: MonitorApplicationContext | null = null;
    private applicationReady = false;
    private applicationOrigin = '';
    private readonly applicationOrigins = new Set<string>();
    private channelId = '';
    private connectionEnabled = false;
    private contextSent = false;
    private destroyed = false;

    private connectionTimer?: ReturnType<typeof setTimeout>;
    private helloTimer?: ReturnType<typeof setInterval>;
    private heartbeatTimer?: ReturnType<typeof setInterval>;
    private pongTimer?: ReturnType<typeof setTimeout>;

    private previousBodyOverflow = '';
    private previousFocus: HTMLElement | null = null;

    private readonly messageListener = (event: MessageEvent): void => {
        this.zone.run(() => {
            if (this.destroyed) return;
            this.receiveMessage(event);
            this.cdr.markForCheck();
        });
    };

    constructor(@Inject(DOCUMENT) private readonly document: Document, private readonly renderer: Renderer2, private readonly sanitizer: DomSanitizer, private readonly zone: NgZone, private readonly route: ActivatedRoute, private readonly projectService: ProjectService, private readonly cdr: ChangeDetectorRef) {}

    get monitorOrigin(): string { return this.document.defaultView?.location.origin ?? ''; }

    ngOnInit(): void {
        this.document.defaultView?.addEventListener('message', this.messageListener);

        this.route.paramMap.pipe(
            switchMap(params => {
                this.resetApplication();
                this.projectId = params.get('id')?.trim() || '';

                if (!this.projectId) {
                    this.errorMessage = 'Route içerisinde proje ID bulunamadı.';
                    this.cdr.markForCheck();
                    return EMPTY;
                }

                this.isLoading = true;
                this.cdr.markForCheck();

                return this.projectService.GetApplication(this.projectId).pipe(
                    tap(result => {
                        if (this.isClosed || this.destroyed) return;

                        if (result.isError || !result.data) {
                            this.errorMessage = result.msg || 'Proje bilgileri alınamadı.';
                            this.cdr.markForCheck();
                            return;
                        }

                        const project = result.data;

                        this.applicationTitle = project.applicationInfo?.name || 'Uygulama';
                        this.applicationVersion = project.applicationInfo?.version || '';
                        this.setApplicationContext(this.createApplicationContext(project));
                        this.setApplicationUrl(project.applicationUrl);
                    }),
                    catchError((error: unknown) => {
                        this.errorMessage = this.getErrorMessage(error);
                        console.error('[Monitor API]', this.errorMessage);
                        this.cdr.markForCheck();
                        return EMPTY;
                    }),
                    finalize(() => {
                        this.isLoading = false;
                        if (!this.destroyed) this.cdr.markForCheck();
                    })
                );
            }),
            takeUntil(this.destroyed$)
        ).subscribe();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroyed$.next();
        this.destroyed$.complete();
        this.disconnect();
        this.document.defaultView?.removeEventListener('message', this.messageListener);
        this.applicationContext = null;
        this.restoreWindow();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void { if (this.isExpanded) this.restoreWindow(); }

    toggleFullscreen(): void {
        if (this.isExpanded) {
            this.restoreWindow();
            return;
        }

        const element = this.monitorWindow.nativeElement as HTMLElement & { showPopover?: () => void };

        this.previousFocus = this.document.activeElement as HTMLElement | null;
        this.previousBodyOverflow = this.document.body.style.overflow;
        this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
        this.isExpanded = true;

        if (typeof element.showPopover === 'function') {
            this.renderer.setAttribute(element, 'popover', 'manual');
            element.showPopover();
        }

        this.cdr.markForCheck();
    }

    onFrameLoad(): void {
        if (this.destroyed || this.isClosed || this.connected) return;
        this.connect();
    }

    connect(): void {
        if (this.destroyed || this.isClosed) return;

        const currentWindow = this.document.defaultView;
        const applicationWindow = this.applicationFrame?.nativeElement.contentWindow;

        if (!currentWindow || !this.safeApplicationUrl || !this.applicationOrigin || !applicationWindow) return;

        // Bağlantı kurulurken tekrar çağrılırsa aynı kanalı kullan.
        if (this.connectionEnabled && this.channelId) {
            if (!this.connected) this.postMessage('MONITOR_HELLO', {});
            return;
        }

        if (!this.applicationContext) {
            this.failConnection('Uygulamaya gönderilecek proje bilgileri bulunamadı.');
            return;
        }

        this.channelId = currentWindow.crypto.randomUUID();
        this.connectionEnabled = true;
        this.beginHandshake();
    }

    disconnect(): void {
        this.postMessage('MONITOR_DISCONNECT', {});
        this.clearConnectionTimers();
        this.connectionEnabled = false;
        this.applicationReady = false;
        this.contextSent = false;
        this.channelId = '';
        this.setConnected(false);
    }

    isConnected(): boolean { return this.connected; }

    closeApplication(): void {
        this.isClosed = true;
        this.disconnect();
        this.restoreWindow();
        this.applicationContext = null;
        this.safeApplicationUrl = null;
        this.applicationOrigin = '';
        this.applicationOrigins.clear();
        this.errorMessage = '';
        this.isLoading = false;
        this.applicationClosed.emit();
        this.cdr.markForCheck();
    }

    sendData(payload: unknown): void { if (this.connected) this.postMessage('MONITOR_DATA', payload); }

    setApplicationContext(context: ApplicationContext): void {
        if (this.destroyed || this.isClosed) return;

        if (context.projectId !== this.projectId) throw new Error('ApplicationContext proje ID değeri açık projeyle eşleşmiyor.');
        if (!context.projectId.trim() || context.projectId.length > 180) throw new Error('ApplicationContext proje ID değeri geçersiz.');
        if (!/^[a-zA-Z-]{2,15}$/.test(context.language)) throw new Error('ApplicationContext dil değeri geçersiz.');
        if (context.theme !== 'light' && context.theme !== 'dark') throw new Error('ApplicationContext tema değeri geçersiz.');

        const tokens = this.readConnectionTokens(context);
        this.applicationContext = {
            ...context,
            projectId: context.projectId,
            language: context.language,
            theme: context.theme,
            accessToken: tokens.accessToken,
            dataToken: tokens.dataToken
        };
        this.sendContext();
    }

    private createApplicationContext(project: unknown): MonitorApplicationContext {
        const tokens = this.readConnectionTokens(project);
        return {
            projectId: this.projectId,
            language: 'tr',
            theme: 'light',
            accessToken: tokens.accessToken,
            dataToken: tokens.dataToken
        } as MonitorApplicationContext;
    }

    private readConnectionTokens(value: unknown): MonitorConnectionTokens {
        if (!this.isRecord(value)) throw new Error('Proje bağlantı bilgileri bulunamadı.');

        const api = this.isRecord(value['api']) ? value['api'] : null;
        const rawAccessToken = value['accessToken'] ?? api?.['accessToken'];
        const rawDataToken = value['dataToken'] ?? api?.['dataToken'];
        const accessToken = typeof rawAccessToken === 'string' ? rawAccessToken.trim() : '';
        const dataToken = typeof rawDataToken === 'string' ? rawDataToken.trim() : '';

        if (!accessToken || !dataToken) {
            const missing = [!accessToken ? 'accessToken' : '', !dataToken ? 'dataToken' : ''].filter(Boolean).join(', ');
            throw new Error(`Proje bağlantı bilgilerinde ${missing} eksik veya boş. GetApplication API yanıtında her iki token da gönderilmelidir.`);
        }

        return { accessToken, dataToken };
    }

    private beginHandshake(): void {
        this.clearConnectionTimers();
        this.setConnected(false);
        this.applicationReady = false;
        this.contextSent = false;
        this.errorMessage = '';

        this.connectionTimer = setTimeout(() => this.zone.run(() => {
            if (this.destroyed || !this.connectionEnabled || this.connected) return;

            const message = this.applicationReady
                ? 'Uygulama APP_READY gönderdi ancak MONITOR_INIT mesajını aldığını APP_CONNECTED ile onaylamadı.'
                : 'Uygulamadan APP_READY alınamadı. Uygulamanın bridge ayarlarını ve MONITOR_ORIGIN değerini kontrol edin.';

            this.failConnection(message);
        }), 15_000);

        this.helloTimer = setInterval(() => this.zone.run(() => {
            if (this.destroyed || !this.connectionEnabled || this.connected) return;
            this.postMessage('MONITOR_HELLO', {});
        }), 1000);

        this.postMessage('MONITOR_HELLO', {});
        this.cdr.markForCheck();
    }

    private sendContext(forceInit = false): void {
        if (!this.connectionEnabled || !this.applicationReady || !this.applicationContext) return;

        const type = forceInit || !this.contextSent ? 'MONITOR_INIT' : 'MONITOR_UPDATE';

        const payload = {
            projectId: this.applicationContext.projectId,
            language: this.applicationContext.language,
            theme: this.applicationContext.theme,
            accessToken: this.applicationContext.accessToken,
            dataToken: this.applicationContext.dataToken,
            applicationUrl: [...this.applicationOrigins]
        };

        if (this.postMessage(type, payload)) {
            this.contextSent = true;
            this.cdr.markForCheck();
        }
    }

    private receiveMessage(event: MessageEvent): void {
        if (this.destroyed || this.isClosed) return;

        const message: unknown = event.data;

        if (!this.isRecord(message) || message['protocol'] !== 'ANAMETA_MONITOR_V1') return;
        if (event.source !== this.applicationFrame?.nativeElement.contentWindow) return;

        if (!this.applicationOrigins.has(event.origin)) {
            console.error('[Monitor] Uygulama origin listesinde bulunamadı.', { gelen: event.origin, izinVerilen: [...this.applicationOrigins] });
            return;
        }

        const type = message['type'];

        if (type === 'APP_DISCOVER') {
            if (event.origin !== this.applicationOrigin) {
                this.disconnect();
                this.applicationOrigin = event.origin;
            }

            if (!this.connectionEnabled) {
                this.connect();
            } else if (this.connected) {
                // İframe yeniden yüklendi veya uygulama tekrar bağlanıyor.
                // Kanal korunur, bağlantı onayı tekrar alınır.
                this.beginHandshake();
            } else {
                this.postMessage('MONITOR_HELLO', {});
            }

            return;
        }

        if (!this.connectionEnabled || !this.channelId) return;

        if (message['channelId'] !== this.channelId) {
            console.warn('[Monitor] Uygulama mesajının channelId değeri mevcut kanalla eşleşmiyor.');
            return;
        }

        if (type === 'APP_READY') {
            if (this.applicationReady && event.origin !== this.applicationOrigin) return;

            this.applicationOrigin = event.origin;
            this.applicationReady = true;
            this.sendContext(true);
            return;
        }

        if (event.origin !== this.applicationOrigin) return;

        if (type === 'APP_CONNECTED') {
            if (!this.applicationReady || !this.contextSent || this.connected) return;

            this.clearConnectionTimers();
            this.errorMessage = '';
            this.setConnected(true);
            this.startHeartbeat();
            this.applicationConnected.emit();
            return;
        }

        if (type === 'APP_PONG' && this.connected) {
            if (this.pongTimer !== undefined) clearTimeout(this.pongTimer);
            this.pongTimer = undefined;
            return;
        }

        if (type === 'APP_ERROR') {
            const payload = message['payload'];
            const errorMessage = this.isRecord(payload) && typeof payload['message'] === 'string' ? payload['message'] : 'Uygulama bağlantıyı reddetti.';
            this.failConnection(errorMessage);
            return;
        }

        if (type === 'APP_DATA' && this.connected) this.applicationData.emit(message['payload']);
    }

    private postMessage(type: string, payload: unknown): boolean {
        if (!this.connectionEnabled || !this.applicationOrigin || !this.channelId) return false;

        const applicationWindow = this.applicationFrame?.nativeElement.contentWindow;

        if (!applicationWindow) return false;

        const message = {
            protocol: 'ANAMETA_MONITOR_V1',
            channelId: this.channelId,
            type,
            monitorOrigin: this.monitorOrigin,
            payload
        };

        // İlk el sıkışmasında iframe listedeki başka bir adrese yönlenmiş olabilir.
        // Proje bilgileri sadece yanıt veren aktif origin adresine gönderilir.
        if (type === 'MONITOR_HELLO' && !this.applicationReady) {
            for (const origin of this.applicationOrigins) applicationWindow.postMessage(message, origin);
        } else {
            applicationWindow.postMessage(message, this.applicationOrigin);
        }

        return true;
    }

    private setConnected(value: boolean): void {
        const changed = this.connected !== value;
        this.connected = value;

        if (changed && !this.destroyed) this.connectionChanged.emit(value);
        if (!this.destroyed) this.cdr.markForCheck();
    }

    private failConnection(message: string): void {
        console.error('[Monitor BLOCKED]', {
            neden: message,
            monitorOrigin: this.monitorOrigin,
            applicationOrigin: this.applicationOrigin,
            applicationReady: this.applicationReady,
            contextSent: this.contextSent
        });

        this.errorMessage = message;
        this.disconnect();
        if (!this.destroyed) this.cdr.markForCheck();
    }

    private startHeartbeat(): void {
        this.heartbeatTimer = setInterval(() => this.zone.run(() => {
            if (this.destroyed || !this.connected || this.document.hidden || this.pongTimer !== undefined) return;

            if (!this.postMessage('MONITOR_PING', {})) {
                this.failConnection('Uygulama penceresine erişilemedi.');
                return;
            }

            this.pongTimer = setTimeout(() => this.zone.run(() => {
                this.pongTimer = undefined;

                if (!this.destroyed && this.connected && !this.document.hidden) {
                    this.failConnection('Uygulamanın bağlantı kontrolüne yanıt alınamadı.');
                }
            }), 12_000);
        }), 5_000);
    }

    private clearConnectionTimers(): void {
        if (this.connectionTimer !== undefined) clearTimeout(this.connectionTimer);
        if (this.helloTimer !== undefined) clearInterval(this.helloTimer);
        if (this.heartbeatTimer !== undefined) clearInterval(this.heartbeatTimer);
        if (this.pongTimer !== undefined) clearTimeout(this.pongTimer);

        this.connectionTimer = undefined;
        this.helloTimer = undefined;
        this.heartbeatTimer = undefined;
        this.pongTimer = undefined;
    }

    private resetApplication(): void {
        this.disconnect();
        this.applicationContext = null;
        this.isClosed = false;
        this.applicationVersion = '';
        this.safeApplicationUrl = null;
        this.applicationOrigin = '';
        this.applicationOrigins.clear();
        this.applicationTitle = 'Uygulama';
        this.errorMessage = '';
        this.isLoading = false;
    }

    private setApplicationUrl(applicationUrl: readonly string[] | null | undefined): void {
        this.safeApplicationUrl = null;
        this.applicationOrigin = '';
        this.applicationOrigins.clear();

        if (!Array.isArray(applicationUrl) || applicationUrl.length === 0) {
            this.errorMessage = 'API cevabındaki applicationUrl alanı dolu bir URL dizisi olmalıdır.';
            this.cdr.markForCheck();
            return;
        }

        try {
            const urls: URL[] = [];

            for (const [index, value] of applicationUrl.entries()) {
                if (typeof value !== 'string' || !value.trim()) throw new Error(`applicationUrl dizisinin ${index + 1}. adresi boş veya geçersiz.`);

                let url: URL;

                try {
                    url = new URL(value.trim());
                } catch {
                    throw new Error(`applicationUrl dizisinin ${index + 1}. adresi geçerli bir mutlak URL olmalıdır.`);
                }

                const localHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);

                if (url.protocol !== 'https:' && !localHttp) throw new Error(`applicationUrl dizisinin ${index + 1}. adresi HTTPS olmalıdır. Localhost için HTTP kullanılabilir.`);
                if (url.username || url.password) throw new Error(`applicationUrl dizisinin ${index + 1}. adresi kullanıcı adı veya parola içeremez.`);

                urls.push(url);
            }

            const initialUrl = urls[0];

            for (const url of urls) this.applicationOrigins.add(url.origin);

            this.applicationOrigin = initialUrl.origin;
            this.safeApplicationUrl = this.sanitizer.bypassSecurityTrustResourceUrl(initialUrl.href);
            this.errorMessage = '';
        } catch (error: unknown) {
            this.applicationOrigins.clear();
            this.applicationOrigin = '';
            this.safeApplicationUrl = null;
            this.errorMessage = error instanceof Error ? error.message : 'Uygulama adresleri geçersiz.';
            console.error('[Monitor URL]', this.errorMessage);
        }

        this.cdr.markForCheck();
    }

    private restoreWindow(): void {
        if (!this.isExpanded) return;

        const element = this.monitorWindow.nativeElement as HTMLElement & { hidePopover?: () => void };

        if (element.hasAttribute('popover')) {
            element.hidePopover?.();
            this.renderer.removeAttribute(element, 'popover');
        }

        this.renderer.setStyle(this.document.body, 'overflow', this.previousBodyOverflow);
        this.isExpanded = false;
        this.previousFocus?.focus({ preventScroll: true });
        this.previousFocus = null;

        if (!this.destroyed) this.cdr.markForCheck();
    }

    private isRecord(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null && !Array.isArray(value); }

    private getErrorMessage(error: unknown): string {
        if (this.isRecord(error)) {
            const response = error['error'];

            if (typeof response === 'string' && response.trim()) return response;

            if (this.isRecord(response)) {
                for (const key of ['msg', 'message', 'detail', 'title']) {
                    const value = response[key];
                    if (typeof value === 'string' && value.trim()) return value;
                }
            }

            const message = error['message'];
            if (typeof message === 'string' && message.trim()) return message;
        }

        return typeof error === 'string' && error.trim() ? error : 'API’den proje bilgileri alınamadı.';
    }
}